import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LabelList, ReferenceLine } from 'recharts';
import './App.css';

// Hook to track window dimensions
function useWindowDimensions() {
    const [dimensions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight
    });
    useEffect(() => {
        let timeoutId;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                setDimensions({
                    width: window.innerWidth,
                    height: window.innerHeight
                });
            }, 150);
        };
        window.addEventListener('resize', handleResize);
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('resize', handleResize);
        };
    }, []);
    return dimensions;
}

// Brand-inspired provider colors — qualitative/Set1-style saturation
// Each color is recognizable as the provider's brand accent
const PROVIDER_COLORS = {
  // openai:    '#6E6E80',  // OpenAI dark grey
  // openai: '#74AA9C',  // OpenAI old logo teal (from logo gradient)
  openai: '#6B6B75	',  // OpenAI new logo gray (from logo)
  anthropic: '#CC785C',  // Anthropic warm terracotta (from logo)
  google:    '#E05243',  // Google lighter red
  meta:      '#1877F2',  // Meta blue
  mistral:   '#EF7B2D',  // Mistral avg orange-amber (from logo gradient)
  cohere:    '#C9A0D2',  // Cohere soft lavender-pink (from logo)
  deepseek:  '#4D6BFE',  // DeepSeek vibrant blue (from logo)
};

// Fallback palette for any unlisted providers (Set2/tab10-inspired)
const FALLBACK_COLORS = [
  '#e41a1c',  // red
  '#66c2a5',  // seafoam
  '#fc8d62',  // salmon
  '#8da0cb',  // periwinkle
  '#a6d854',  // lime
  '#ffd92f',  // gold
  '#999999',  // gray
];

const RETRIEVER_NOTE = ' All runs use a strict retriever (top-k=3) to emphasize differences between LLMs. In production chatbots, we use higher retrieval values, so real-world accuracy is even higher than shown here.';

const EXPERIMENT_DESCRIPTIONS = {
  'helvia-ragc-hr700': 'Contains 700 real-world question/answer pairs related to the domain of human resources.' + RETRIEVER_NOTE,
  'helvia-ragc-banking500': 'Contains 500 real-world question/answer pairs related to the domain of banking.' + RETRIEVER_NOTE,
  'helvia-ragc-gaming2000': 'Contains 2000 real-world question/answer pairs related to the domain of interactive entertainment (gaming).' + RETRIEVER_NOTE,
};

function App() {
  const { width } = useWindowDimensions();

  // Theme state: persist to localStorage, default to system preference
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('grace-theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const isDark = theme === 'dark';

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('grace-theme', theme);
    // Update meta theme-color for browser chrome
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'theme-color';
      document.head.appendChild(meta);
    }
    meta.content = theme === 'dark' ? '#24262F' : '#F9F7F4';
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  }, []);

  // Three-tier breakpoint system
  const breakpoint = width < 600 ? 'mobile' : width < 1024 ? 'tablet' : 'desktop';
  const isMobile = breakpoint === 'mobile';
  const isTablet = breakpoint === 'tablet';

  // Stepped font sizes per breakpoint
  const tickFontSize = isMobile ? 9 : isTablet ? 11 : 13;
  const yAxisLabelFontSize = isMobile ? 11 : isTablet ? 13 : 16;
  const tooltipFontSize = isMobile ? 11 : isTablet ? 13 : 14;
  const labelListFontSize = isMobile ? 7 : isTablet ? 9 : 11;

  // Fixed chart height per breakpoint
  const chartHeight = isMobile ? 320 : isTablet ? 380 : 450;

  // Minimal fixed margins
  const dynamicMargin = {
    top: isMobile ? 15 : isTablet ? 20 : 25,
    right: isMobile ? 10 : 20,
    left: isMobile ? 10 : 20,
    bottom: 5
  };

  // Stepped YAxis width
  const yAxisWidthResponsive = isMobile ? 50 : isTablet ? 65 : 80;

  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({});
  const [openFilter, setOpenFilter] = useState(null);

  // Provider-to-color mapping: brand colors first, fallback for unknown providers
  const providerColorMap = useMemo(() => {
    let fallbackIndex = 0;
    const dynamicMap = {};
    return (provider) => {
      const key = provider.toLowerCase();
      if (PROVIDER_COLORS[key]) return PROVIDER_COLORS[key];
      if (!dynamicMap[key]) {
        dynamicMap[key] = FALLBACK_COLORS[fallbackIndex % FALLBACK_COLORS.length];
        fallbackIndex++;
      }
      return dynamicMap[key];
    };
  }, []);

  useEffect(() => {
    const tryFetch = async () => {
      const paths = [
        `results.json?cb=${Date.now()}`,
        `/labs/grace-leaderboard/results.json?cb=${Date.now()}`
      ];

      for (const path of paths) {
        try {
          const response = await fetch(path);
          if (response.ok) {
            const json = await response.json();
            processData(json);
            console.log(`Successfully loaded data from: ${path}`);
            return;
          }
        } catch (error) {
          console.warn(`Failed to load from ${path}:`, error);
        }
      }

      console.error('Failed to load leaderboard data from all sources');
      setLoading(false);
    };

    const processData = (json) => {
      const initialFilters = {};

      const experimentsArray = Object.keys(json).map((experimentName) => {
        const experiment = json[experimentName];

        const datasetFriendlyName = experiment.dataset_friendly_name;

        const metricToVisualize =
          experiment.visualize_metrics && experiment.visualize_metrics.length > 0
            ? experiment.visualize_metrics[0]
            : 'accuracy';

        const metricName = experiment.metric_display_name ||
          metricToVisualize.charAt(0).toUpperCase() + metricToVisualize.slice(1);

        const allRunsData = Object.values(experiment.runs)
          .map((run, index) => {
            const rawMetricValue = typeof run.metrics[metricToVisualize] === 'number'
              ? run.metrics[metricToVisualize]
              : parseFloat(run.metrics[metricToVisualize]) || 0;
            const formattedMetricValue = Math.round(rawMetricValue * 1000) / 10;
            const providerName = run.provider || `provider-${index}`;
            return {
              friendly_name: run.friendly_name,
              metricValue: rawMetricValue,
              displayMetricValue: formattedMetricValue,
              fill: providerColorMap(providerName),
              provider: providerName,
              defaultVisible: !run.exclude_from_visualization
            };
          });

        allRunsData.sort((a, b) => b.metricValue - a.metricValue);

        // Initialize filter with default-visible runs
        initialFilters[experimentName] = new Set(
          allRunsData.filter(r => r.defaultVisible).map(r => r.friendly_name)
        );

        return {
          id: experimentName,
          datasetFriendlyName,
          metricName,
          allData: allRunsData
        };
      });

      setFilters(initialFilters);
      setExperiments(experimentsArray);
      setLoading(false);
    };

    tryFetch();
  }, [providerColorMap]);

  // Filter helpers
  const toggleModel = useCallback((expId, modelName) => {
    setFilters(prev => {
      const next = new Set(prev[expId]);
      if (next.has(modelName)) next.delete(modelName);
      else next.add(modelName);
      return { ...prev, [expId]: next };
    });
  }, []);

  const setAllModels = useCallback((expId, models) => {
    setFilters(prev => ({ ...prev, [expId]: new Set(models) }));
  }, []);

  // Close filter dropdown on outside click
  const filterRef = useRef(null);
  useEffect(() => {
    if (!openFilter) return;
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setOpenFilter(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [openFilter]);

  // Custom tooltip styled for dossier theme
  const DossierTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    return (
      <div style={{
        background: isDark ? '#1A1B23' : 'var(--white)',
        border: isDark ? '2px solid #7B78FF' : '2px solid var(--slate)',
        borderRadius: 'var(--radius)',
        padding: '10px 14px',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : 'var(--shadow)',
        fontSize: tooltipFontSize,
        fontFamily: "'Noto Sans', sans-serif",
      }}>
        <div style={{ fontWeight: 700, color: isDark ? '#E0E2F0' : 'var(--slate)', marginBottom: 4 }}>{label}</div>
        <div style={{ color: isDark ? '#9590FF' : 'var(--purple)', fontWeight: 800, fontSize: tooltipFontSize + 2 }}>
          {Math.round(payload[0].value * 1000) / 10}%
        </div>
      </div>
    );
  };

  return (
    <main className="dossier-page">
      <a href="#charts" className="dossier-skip-link">Skip to charts</a>

      {/* Theme Toggle */}
      <button
        className="dossier-theme-toggle"
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDark ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </button>
      {/* Header */}
      <header className="dossier-header">
        <h1 className="dossier-header-title">
          GRACE Leaderboard <span className="dossier-header-title-sub">by helvia.ai</span>
        </h1>
        <hr className="dossier-header-divider" />
        <p className="dossier-header-desc">
          How do different LLMs compare when used in RAG-enabled chatbots?
          Using GRACE (Grounded Retrieval-Augmented Citation Evaluation), a technique we introduced at our labs,
          we can evaluate the question-answering quality of LLMs by citation matching.{' '}
          <a href="https://helvia.ai/labs/rag-with-citations-and-grace/" target="_blank" rel="noreferrer">
            Read the full report at helvia.ai/labs
          </a>.
        </p>
      </header>

      {/* Loading State */}
      {loading && (
        <div className="dossier-loading">
          <div className="dossier-loading-spinner" />
          Loading leaderboard data...
        </div>
      )}
      {!loading && experiments.length === 0 && (
        <div className="dossier-loading">No leaderboard data available.</div>
      )}

      {/* Experiment Cards */}
      <div id="charts">
        {experiments.map((exp, expIndex) => {
          const expFilters = filters[exp.id] || new Set();
          const visibleData = exp.allData.filter(d => expFilters.has(d.friendly_name));
          const providers = Array.from(new Set(visibleData.map(d => d.provider)));
          const description = EXPERIMENT_DESCRIPTIONS[exp.datasetFriendlyName];
          const isFiltered = visibleData.length !== exp.allData.length;
          const isFilterOpen = openFilter === exp.id;

          return (
            <article key={exp.id} className="dossier-card">
              {/* Compact header: title row with legend */}
              <div className="dossier-card-toprow">
                <div className="dossier-card-title-group">
                  <span className="dossier-card-label">Experiment #{expIndex + 1}</span>
                  <h2 className="dossier-card-title">
                    {exp.datasetFriendlyName}
                    {description && (
                      <span className="dossier-info-trigger" aria-label="Dataset info">
                        ?
                        <span className="dossier-info-bubble">{description}</span>
                      </span>
                    )}
                  </h2>
                </div>
                <div className="dossier-card-meta">
                  <div className="dossier-card-meta-row">
                    <span className="dossier-model-count">
                      {visibleData.length === 0
                        ? `0 / ${exp.allData.length} models`
                        : visibleData.length === 1
                          ? '1 model'
                          : `${visibleData.length} models`}
                    </span>
                    <div className="dossier-filter-wrap" ref={isFilterOpen ? filterRef : null}>
                      <button
                        className={`dossier-filter-btn${isFiltered ? ' dossier-filter-btn--active' : ''}`}
                        onClick={() => setOpenFilter(isFilterOpen ? null : exp.id)}
                        aria-label="Filter models"
                        title="Select which models to show or hide in the chart"
                      >
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1.5 2h13M3.5 5.5h9M5.5 9h5M7 12.5h2" />
                        </svg>
                      </button>
                      {isFilterOpen && (
                        <div className="dossier-filter-dropdown">
                          <div className="dossier-filter-header">
                            <span className="dossier-filter-title">Filter models</span>
                            <span className="dossier-filter-actions">
                              <button
                                className="dossier-filter-action"
                                onClick={() => setAllModels(exp.id, exp.allData.map(d => d.friendly_name))}
                              >All</button>
                              <button
                                className="dossier-filter-action"
                                onClick={() => setAllModels(exp.id, exp.allData.filter(d => d.defaultVisible).map(d => d.friendly_name))}
                              >Default</button>
                              <button
                                className="dossier-filter-action"
                                onClick={() => setAllModels(exp.id, [])}
                              >None</button>
                            </span>
                          </div>
                          <div className="dossier-filter-list">
                            {exp.allData.map((model) => (
                              <label key={model.friendly_name} className="dossier-filter-item">
                                <input
                                  type="checkbox"
                                  checked={expFilters.has(model.friendly_name)}
                                  onChange={() => toggleModel(exp.id, model.friendly_name)}
                                />
                                <span className="dossier-filter-swatch" style={{ background: model.fill }} />
                                <span className="dossier-filter-name">{model.friendly_name}</span>
                                <span className="dossier-filter-value">{model.displayMetricValue}%</span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <ul className="dossier-legend">
                    {providers.map((provider) => (
                      <li key={provider} className="dossier-legend-item">
                        <span
                          className="dossier-legend-swatch"
                          style={{ background: providerColorMap(provider) }}
                        />
                        {provider}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Chart */}
              <div role="img" aria-label={`${exp.datasetFriendlyName} — ${exp.metricName} bar chart`}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={visibleData} margin={dynamicMargin}>
                    <CartesianGrid
                      strokeDasharray="4 4"
                      stroke={isDark ? 'rgba(120, 120, 180, 0.2)' : 'rgba(194, 194, 232, 0.75)'}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="friendly_name"
                      angle={isMobile ? -60 : -45}
                      textAnchor="end"
                      height={isMobile ? 100 : isTablet ? 120 : 140}
                      tick={{ fontSize: tickFontSize, fill: isDark ? '#D0D4E8' : '#505776', fontWeight: 600 }}
                      interval={0}
                      axisLine={{ stroke: isDark ? '#D0D4E8' : '#505776', strokeWidth: 1.5 }}
                      tickLine={{ stroke: isDark ? '#D0D4E8' : '#505776' }}
                    />
                    <YAxis
                      label={{
                        value: `${exp.metricName} (%)`,
                        angle: -90,
                        position: 'insideLeft',
                        offset: 10,
                        fontSize: yAxisLabelFontSize,
                        dy: 60,
                        fill: isDark ? '#D0D4E8' : '#505776',
                        fontWeight: 600,
                      }}
                      tickFormatter={(value) => `${Math.round(value * 1000) / 10}`}
                      domain={[0.4, 1]}
                      ticks={[0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]}
                      interval={0}
                      tick={{ fontSize: yAxisLabelFontSize, fontVariantNumeric: 'tabular-nums', fill: isDark ? '#D0D4E8' : '#505776' }}
                      width={yAxisWidthResponsive}
                      allowDecimals={true}
                      axisLine={{ stroke: isDark ? '#D0D4E8' : '#505776', strokeWidth: 1.5 }}
                      tickLine={{ stroke: isDark ? '#D0D4E8' : '#505776' }}
                    />
                    <Tooltip content={<DossierTooltip />} cursor={{ fill: isDark ? 'rgba(123, 120, 255, 0.12)' : 'rgba(96, 93, 236, 0.06)' }} />
                    {visibleData.map((entry, index) => (
                      <ReferenceLine key={index} x={entry.friendly_name} stroke={isDark ? 'rgba(120, 120, 180, 0.12)' : 'rgba(194, 194, 232, 0.25)'} />
                    ))}
                    <Bar
                      dataKey="metricValue"
                      name={exp.metricName}
                      stroke={isDark ? '#C8CDE0' : '#333333'}
                      strokeWidth={2}
                      barSize={isMobile ? 10 : isTablet ? 16 : 22}
                      radius={[3, 3, 0, 0]}
                    >
                      <LabelList
                        dataKey="displayMetricValue"
                        position="top"
                        formatter={(value) => `${value}%`}
                        style={{
                          fontSize: labelListFontSize,
                          fill: isDark ? '#D0D4E8' : '#505776',
                          fontWeight: 700,
                          fontVariantNumeric: 'tabular-nums',
                          fontFamily: "'Noto Sans', sans-serif"
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </article>
          );
        })}
      </div>

      {/* Footer */}
      {!loading && experiments.length > 0 && (
        <footer className="dossier-footer">
          <p className="dossier-footer-text">
            GRACE Leaderboard by{' '}
            <a href="https://helvia.ai" target="_blank" rel="noreferrer">helvia.ai</a>
          </p>
        </footer>
      )}
    </main>
  );
}

export default App;
